import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { 
    doc, getDoc, setDoc, collection, getDocs, query, orderBy, limit, startAfter, getCountFromServer 
} from "firebase/firestore";
import { db } from "../../firebase";

export const StorageCartKey = "catalogo_cart";

export const getUserData = createAsyncThunk(
    'app/getUserData',
    async (userID, { rejectWithValue }) => {
        try {
            const docRef = doc(db, `Miembros/${userID}`);
            const snapshot = await getDoc(docRef);
            if (!snapshot.exists()) return null;
            let data = { id: snapshot.id, ...snapshot.data() };
            delete data.createdAt;
            return data;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const getCatalogData = createAsyncThunk(
    'app/getCatalogData',
    async ({ userID, catalogID }, { rejectWithValue, dispatch }) => {
        try {
            const docRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}`);
            const collectionRef = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos`);
            
            const snapshot = await getDoc(docRef);
            const countSnap = await getCountFromServer(collectionRef);

            // Get max price for filtering
            const queryRef = query(collectionRef, orderBy("price", "desc"), limit(1));
            const products = await getDocs(queryRef);
            const maxPriceProduct = products.docs[0] ? products.docs[0].data() : { price: 0 };
            dispatch(setMaxPrice(maxPriceProduct.price || 0));

            const catalog = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
            if (catalog?.name) {
                document.title = catalog.name;
            }

            return {
                ...catalog,
                totalProducts: countSnap.data().count
            };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const getProductsBatch = createAsyncThunk(
    'app/getProductsBatch',
    async ({ userID, catalogID, size = 12, lastRef = null }, { rejectWithValue }) => {
        try {
            const productsRef = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos`);
            let batchQuery = lastRef 
                ? query(productsRef, orderBy("position"), startAfter(lastRef), limit(size))
                : query(productsRef, orderBy("position"), limit(size));

            let batchSnap;
            try {
                batchSnap = await getDocs(batchQuery);
            } catch (err) {
                // Fallback to ordering by name if position index doesn't exist yet
                batchQuery = lastRef 
                    ? query(productsRef, orderBy("name"), startAfter(lastRef), limit(size))
                    : query(productsRef, orderBy("name"), limit(size));
                batchSnap = await getDocs(batchQuery);
            }

            const products = await Promise.all(batchSnap.docs.map(async (docSnap) => {
                const productData = { id: docSnap.id, ...docSnap.data(), pictures: {} };
                
                // Fetch Images subcollection for this product concurrently
                try {
                    const imagesSubRef = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${docSnap.id}/Images`);
                    const imgSnap = await getDocs(imagesSubRef);
                    imgSnap.docs.forEach(imgDoc => {
                        productData.pictures[imgDoc.id] = imgDoc.data();
                    });
                } catch (imgErr) {
                    console.error("Error fetching images for product:", docSnap.id, imgErr);
                }

                return productData;
            }));

            return {
                products,
                lastRef: batchSnap.docs[batchSnap.docs.length - 1] || null
            };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const getImage = createAsyncThunk(
    'app/getImage',
    async ({ userID, catalogID, productID, imageID }, { rejectWithValue }) => {
        try {
            const imageRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}/Images/${imageID}`);
            const result = await getDoc(imageRef);
            if (result.exists()) {
                return { productID, imageID, data: result.data() };
            }
            return { productID, imageID, data: null };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

// Dev-Only Place Order Async Thunk
export const placeOrder = createAsyncThunk(
    'app/placeOrder',
    async ({ sellerUID, catalogID, catalogName, clientName, clientPhone, clientEmail, items, currency, total, sendWhatsapp }, { rejectWithValue }) => {
        try {
            const orderRef = doc(collection(db, `Miembros/${sellerUID}/Pedidos`));
            const orderPayload = {
                id: orderRef.id,
                catalogID: catalogID || '',
                catalogName: catalogName || 'Catálogo',
                clientName,
                clientPhone: clientPhone || '',
                clientEmail: clientEmail || '',
                items,
                currency: currency || '$',
                total,
                status: 'pending',
                whatsappSent: !!sendWhatsapp,
                createdAt: new Date().toISOString()
            };

            await setDoc(orderRef, orderPayload);
            return orderPayload;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

const initialState = {
    user: { isLoading: false, value: null },
    catalog: { isLoading: false, value: null },
    products: {
        ids: [],
        entities: {},
        filteredIds: null,
        total: 0,
        lastProductRef: null,
        hasMore: true,
        isLoading: false
    },
    gui: {
        selectedProductId: null,
        isDetailOpen: false,
        isCartOpen: false,
        isInfoOpen: false
    },
    cart: {},
    cartRestored: false,
    filters: {
        search: '',
        onlyNonDiscounted: false,
        onlyDiscounted: false,
        stock: 'all',
        maxPrice: 0,
        priceRange: null
    }
};

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        setMaxPrice(state, action) {
            state.filters.maxPrice = action.payload;
        },
        addToCart(state, action) {
            const productId = action.payload;
            state.cart[productId] = (state.cart[productId] || 0) + 1;
            saveCart(state.cart, state.catalog.value?.id || "default");
        },
        removeFromCart(state, action) {
            const productId = action.payload;
            if (state.cart[productId]) {
                if (state.cart[productId] > 1) {
                    state.cart[productId]--;
                } else {
                    delete state.cart[productId];
                }
            }
            saveCart(state.cart, state.catalog.value?.id || "default");
        },
        clearFromCart(state, action) {
            delete state.cart[action.payload];
            saveCart(state.cart, state.catalog.value?.id || "default");
        },
        clearCart(state) {
            state.cart = {};
            saveCart({}, state.catalog.value?.id || "default");
        },
        restoreCart(state, action) {
            const catalogID = action.payload;
            const saved = localStorage.getItem(`${StorageCartKey}_${catalogID}`);
            if (saved) {
                try {
                    state.cart = JSON.parse(saved);
                } catch (e) {
                    state.cart = {};
                }
            } else {
                state.cart = {};
            }
            state.cartRestored = true;
        },
        setSearchFilter(state, action) {
            state.filters.search = action.payload;
            applyFilters(state);
        },
        setStockFilter(state, action) {
            state.filters.stock = action.payload;
            applyFilters(state);
        },
        setPriceRangeFilter(state, action) {
            state.filters.priceRange = action.payload;
            applyFilters(state);
        },
        toggleDiscountedFilter(state, action) {
            if (action.payload) state.filters.onlyNonDiscounted = false;
            state.filters.onlyDiscounted = action.payload;
            applyFilters(state);
        },
        toggleNonDiscountedFilter(state, action) {
            if (action.payload) state.filters.onlyDiscounted = false;
            state.filters.onlyNonDiscounted = action.payload;
            applyFilters(state);
        },
        clearFilters(state) {
            state.filters = {
                search: '',
                onlyNonDiscounted: false,
                onlyDiscounted: false,
                stock: 'all',
                maxPrice: state.filters.maxPrice,
                priceRange: null
            };
            state.products.filteredIds = null;
        },
        setSelectedProduct(state, action) {
            state.gui.selectedProductId = action.payload;
            state.gui.isDetailOpen = !!action.payload;
        },
        toggleCartOpen(state, action) {
            state.gui.isCartOpen = typeof action.payload === 'boolean' ? action.payload : !state.gui.isCartOpen;
        }
    },
    extraReducers: (builder) => {
        builder
            // User
            .addCase(getUserData.pending, (state) => {
                state.user.isLoading = true;
            })
            .addCase(getUserData.fulfilled, (state, action) => {
                state.user.isLoading = false;
                state.user.value = action.payload;
            })
            .addCase(getUserData.rejected, (state) => {
                state.user.isLoading = false;
                state.user.value = null;
            })
            // Catalog
            .addCase(getCatalogData.pending, (state) => {
                state.catalog.isLoading = true;
            })
            .addCase(getCatalogData.fulfilled, (state, action) => {
                state.catalog.isLoading = false;
                state.catalog.value = action.payload;
            })
            .addCase(getCatalogData.rejected, (state) => {
                state.catalog.isLoading = false;
                state.catalog.value = null;
            })
            // Products
            .addCase(getProductsBatch.pending, (state) => {
                state.products.isLoading = true;
            })
            .addCase(getProductsBatch.fulfilled, (state, action) => {
                state.products.isLoading = false;
                const { products, lastRef } = action.payload;
                products.forEach(p => {
                    if (!state.products.entities[p.id]) {
                        state.products.ids.push(p.id);
                    }
                    state.products.entities[p.id] = p;
                });
                state.products.lastProductRef = lastRef;
                const requestedSize = action.meta.arg.size || 12;
                if (products.length < requestedSize) {
                    state.products.hasMore = false;
                }
                applyFilters(state);
            })
            .addCase(getProductsBatch.rejected, (state) => {
                state.products.isLoading = false;
            })
            // Image
            .addCase(getImage.fulfilled, (state, action) => {
                const { productID, imageID, data } = action.payload;
                if (state.products.entities[productID] && data) {
                    if (!state.products.entities[productID].pictures) {
                        state.products.entities[productID].pictures = {};
                    }
                    state.products.entities[productID].pictures[imageID] = data;
                }
            });
    }
});

function applyFilters(state) {
    state.products.filteredIds = state.products.ids.filter(id => {
        const product = state.products.entities[id];
        if (!product) return false;

        if (state.filters.search.trim() !== '') {
            const search = state.filters.search.trim().toLowerCase();
            const nameMatch = product.name?.toLowerCase().includes(search);
            const descMatch = product.description?.toLowerCase().includes(search);
            const codeMatch = product.code?.toLowerCase().includes(search);
            if (!nameMatch && !descMatch && !codeMatch) {
                return false;
            }
        }
        if (state.filters.onlyDiscounted && (!product.discount || product.discount <= 0)) {
            return false;
        }
        if (state.filters.onlyNonDiscounted && product.discount > 0) {
            return false;
        }
        if (state.filters.stock !== 'all' && product.stock !== state.filters.stock) {
            return false;
        }
        if (state.filters.priceRange) {
            const [min, max] = state.filters.priceRange;
            const finalPrice = product.price - (product.price * ((product.discount || 0) / 100));
            if (finalPrice < min || finalPrice > max) {
                return false;
            }
        }
        return true;
    });
}

function saveCart(cart, uniqueID) {
    localStorage.setItem(`${StorageCartKey}_${uniqueID}`, JSON.stringify(cart));
}

export const {
    setMaxPrice, addToCart, removeFromCart, clearFromCart, clearCart, restoreCart,
    setSearchFilter, setStockFilter, setPriceRangeFilter, toggleDiscountedFilter,
    toggleNonDiscountedFilter, clearFilters, setSelectedProduct, toggleCartOpen
} = appSlice.actions;

export default appSlice.reducer;
